import { useEffect, useState } from 'react';

import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';
import { formatCurrency } from '../blocos/tipos';
import type { DadosInicio } from './useDadosInicio';
import type { ObjetivoDoMes } from './useObjetivoDoMes';
import { CardBento, CabecalhoLista, Sub } from './ui';
import { useMascara } from '../../valores-ocultos/contexto';

/**
 * Equipe no mês — o card de gestão do v3, que junta três caixas antigas:
 * o OBJETIVO da equipe (barra + faltam + ritmo em R$/dia), o ranking (as
 * barras por vendedor, vindas de vendedor_producao_mensal) e, pro super
 * admin, a linha de comissões pagas.
 *
 * "Objetivo" em todo lugar — decisão do usuário (2026-08-25): a palavra
 * "meta" saiu do dashboard, inclusive pra gestão.
 */
const TOP = 3;

type LinhaRanking = { id: number; nome: string; producao: number };

export const CardEquipeNoMes = ({
  data,
  objetivo,
  isSuperAdmin,
  navigate,
}: {
  data: DadosInicio;
  objetivo: ObjetivoDoMes | null;
  isSuperAdmin: boolean;
  navigate: (to: string) => void;
}) => {
  const m = useMascara();
  const [ranking, setRanking] = useState<LinhaRanking[] | null>(null);

  useEffect(() => {
    let ativo = true;
    (async () => {
      const agora = new Date();
      const mes = `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}`;
      const [prodRes, usersRes] = await Promise.all([
        supabase
          .from('vendedor_producao_mensal')
          .select('sales_person_id, producao')
          .eq('mes', mes),
        supabase.from('users').select('id, name'),
      ]);
      if (!ativo) return;
      const nomePorId = new Map(
        ((usersRes.data ?? []) as { id: number; name: string | null }[]).map((u) => [
          u.id,
          u.name ?? '',
        ]),
      );
      setRanking(
        ((prodRes.data ?? []) as { sales_person_id: number | null; producao: number | string | null }[])
          .filter((r) => r.sales_person_id != null && Number(r.producao) > 0)
          .map((r) => ({
            id: r.sales_person_id as number,
            nome: nomePorId.get(r.sales_person_id as number) || 'Sem nome',
            producao: Number(r.producao),
          }))
          .sort((a, b) => b.producao - a.producao),
      );
    })();
    return () => {
      ativo = false;
    };
  }, []);

  const agora = new Date();
  const diaHoje = agora.getDate();
  const diasNoMes = new Date(agora.getFullYear(), agora.getMonth() + 1, 0).getDate();
  const mediaDia = diaHoje > 0 ? data.vendasMesValor / diaHoje : 0;
  const precisaDia =
    objetivo && objetivo.falta > 0 && diasNoMes > diaHoje
      ? objetivo.falta / (diasNoMes - diaHoje)
      : null;

  const maior = ranking?.[0]?.producao ?? 0;

  return (
    <CardBento col={2} className="!px-5 !py-4">
      <CabecalhoLista
        titulo="Equipe no mês"
        extra={
          objetivo
            ? `${objetivo.diasRestantes} ${objetivo.diasRestantes === 1 ? 'dia restante' : 'dias restantes'}`
            : undefined
        }
      />

      {objetivo ? (
        <div className="mt-3">
          <div className="h-1.5 overflow-hidden rounded-full bg-[rgba(53,70,96,0.12)]">
            <div
              className="h-full rounded-full bg-primary"
              style={{ width: `${Math.min(100, Math.max(0, objetivo.pct))}%` }}
            />
          </div>
          <Sub className="!mt-1.5">
            objetivo{' '}
            <span className="font-semibold tabular-nums text-foreground">
              {m(formatCurrency(objetivo.alvo, true))}
            </span>
            {objetivo.falta > 0 ? (
              <>
                {' · '}faltam{' '}
                <span className="font-semibold tabular-nums text-foreground">
                  {m(formatCurrency(objetivo.falta, true))}
                </span>
              </>
            ) : (
              <>
                {' · '}
                <span className="font-semibold text-[#166534]">objetivo batido</span>
              </>
            )}
            {objetivo.ritmoPct != null && (
              <>
                {' · '}
                <span
                  className={
                    objetivo.ritmoPct >= 100
                      ? 'font-semibold text-[#166534]'
                      : 'font-semibold text-[#b45309]'
                  }
                >
                  ritmo fecha em {m(`${objetivo.ritmoPct}%`)}
                </span>
              </>
            )}
          </Sub>
          {precisaDia != null && (
            <Sub className="!mt-0.5">
              média {m(formatCurrency(mediaDia, true))}/dia · precisa de{' '}
              {m(formatCurrency(precisaDia, true))}/dia
            </Sub>
          )}
        </div>
      ) : (
        <Sub className="mt-3">
          Nenhum objetivo definido para a equipe neste mês. Cadastro em Campanhas →
          Resumo.
        </Sub>
      )}

      <div className="mt-3">
        {ranking == null ? (
          <div className="h-[104px]" />
        ) : ranking.length === 0 ? (
          <Sub>Nenhuma produção neste mês.</Sub>
        ) : (
          ranking.slice(0, TOP).map((l, i) => (
            <div
              key={l.id}
              className="flex items-center gap-3 border-b border-[rgba(53,70,96,0.07)] py-1.5 last:border-b-0"
            >
              <span
                className="w-5 shrink-0 text-xs font-bold"
                style={{ color: i === 0 ? '#BEB692' : '#9aa2ad' }}
              >
                {i + 1}
              </span>
              <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">{l.nome}</span>
              <div className="h-1.5 w-[110px] shrink-0 overflow-hidden rounded-full bg-[rgba(53,70,96,0.1)]">
                <div
                  className="h-full rounded-full bg-primary"
                  style={{ width: `${Math.max(4, Math.round((l.producao * 100) / (maior || 1)))}%` }}
                />
              </div>
              <span className="w-[104px] shrink-0 text-right text-[12.5px] font-semibold tabular-nums">
                {m(formatCurrency(l.producao, true))}
              </span>
            </div>
          ))
        )}
      </div>

      {isSuperAdmin && (
        <button
          type="button"
          onClick={() => navigate('/comissoes')}
          className="mt-2.5 block w-full cursor-pointer border-t border-[rgba(53,70,96,0.09)] pt-2.5 text-left"
        >
          <Sub className="!mt-0">
            Comissões pagas no mês:{' '}
            <span className="font-semibold tabular-nums text-foreground">
              {m(formatCurrency(data.comissoesMes, true))}
            </span>{' '}
            <span className="ml-1 rounded-full border border-dashed border-[rgba(53,70,96,0.28)] px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wider text-muted-foreground">
              apenas super admin
            </span>
          </Sub>
        </button>
      )}
    </CardBento>
  );
};
